import React, { useContext } from 'react';
import { NotificationContext } from 'contexts/notification';
import { MultiMediaDeleteDTO } from 'artifacts/dtos';
import { useConfirmDialog } from 'hooks/useHook';
import { AlertVariant } from 'components/common/Alerts';
import { useDeleteMultiMedia } from 'hooks/useApi';

type ConfirmationMessageProps = {
  fileName: string;
}

type MediaMetaItem = {
  originatorType?: string | null;
  mediaId?: number;
};

type DeleteMediaMeta = {
  list?: MediaMetaItem[] | null;
};

function ConfirmationMessage({ fileName }: ConfirmationMessageProps) {
  return (
    <p>
      Are you sure you want to delete
      {' '}
      <strong>{fileName}</strong>
      ?
    </p>
  );
}

export default function useDeleteMediaHandler(refetch?: () => Promise<void>) {
  const { notify } = useContext(NotificationContext);
  const { confirm, closeConfirm } = useConfirmDialog();
  const { mutateAsync: deleteMultiMedia } = useDeleteMultiMedia();

  const handleDelete = async (metaInfo: DeleteMediaMeta) => {
    try {
      const payload: MultiMediaDeleteDTO = {
        list: (metaInfo?.list ?? []).map((m) => ({
          originatorType: m.originatorType ?? null,
          mediaId: m.mediaId ?? 0,
        })),
      };
      await deleteMultiMedia(payload);
      if (typeof refetch === 'function') {
        refetch();
      }

      notify({
        message: 'Media was deleted successfully.',
        variant: AlertVariant.SUCCESS,
      });
    } catch (e) {
      notify({
        message: 'Something went wrong with media deletion.',
        variant: AlertVariant.ERROR,
      });
    }
    closeConfirm();
  };

  const onMediaDelete = (metaInfo: DeleteMediaMeta, fileName: string = '') => {
    confirm({
      title: 'Delete Multimedia',
      content: (<ConfirmationMessage fileName={fileName} />),
      onConfirm: async () => {
        await handleDelete(metaInfo);
      },
    });
  };

  return { onMediaDelete };
}
